// src/pages/ServiceKeysPage.js

import React, { useState, useEffect } from 'react';
import api from '../api/api';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

export default function ServiceKeysPage() {
  // Estado de llaves
  const [keys, setKeys]           = useState([]);
  const [loadingKeys, setLoadingKeys] = useState(false);
  // Estado de tenants para el select
  const [tenants, setTenants]     = useState([]);
  const [loadingTenants, setLoadingTenants] = useState(false);
  // Diálogo de creación
  const [openNew, setOpenNew]     = useState(false);
  const [selectedTenant, setSelectedTenant] = useState('');
  const [creating, setCreating]   = useState(false);
  // Notificaciones
  const [snack, setSnack]         = useState({ open: false, message: '', severity: 'success' });

  // Cargar llaves
  const fetchKeys = async () => {
    setLoadingKeys(true);
    try {
      const res = await api.get('/service_keys');
      setKeys(res.data);
    } catch (err) {
      console.error(err);
      setSnack({ open: true, message: 'Error cargando llaves', severity: 'error' });
    } finally {
      setLoadingKeys(false);
    }
  };

  // Cargar lista de tenants
  const fetchTenants = async () => {
    setLoadingTenants(true);
    try {
      // traemos hasta 100 tenants para el select
      const res = await api.get('/tenants', { params: { page: 1, limit: 100 } });
      setTenants(res.data.tenants);
    } catch (err) {
      console.error(err);
      setSnack({ open: true, message: 'Error cargando tenants', severity: 'error' });
    } finally {
      setLoadingTenants(false);
    }
  };

  useEffect(() => {
    fetchKeys();
    fetchTenants();
  }, []);

  // Crear nueva Service‑Key
  const handleCreate = async () => {
    if (!selectedTenant) {
      setSnack({ open: true, message: 'Por favor selecciona un tenant', severity: 'warning' });
      return;
    }
    setCreating(true);
    try {
      const { data } = await api.post('/service_keys', {
        tenantid: selectedTenant,
        description: tenants.find(t => t.id === selectedTenant)?.name || ''
      });
      // copiar secreto al portapapeles
      navigator.clipboard.writeText(data.service_key);
      setSnack({
        open: true,
        message: '🔑 Key copiada al portapapeles (sólo se muestra una vez), para conectarse utilice tenantid + key en el header',
        severity: 'success'
      });
      setOpenNew(false);
      setSelectedTenant('');
      fetchKeys();
    } catch (err) {
      console.error(err);
      setSnack({
        open: true,
        message: err.response?.data?.message || 'Error creando llave',
        severity: 'error'
      });
    } finally {
      setCreating(false);
    }
  };

  // Revocar llave
  const handleRevoke = async id => {
    if (!window.confirm('¿Seguro que quieres revocar esta llave?')) return;
    try {
      await api.delete(`/service_keys/${id}`);
      setSnack({ open: true, message: 'Service‑Key revocada', severity: 'info' });
      fetchKeys();
    } catch (err) {
      console.error(err);
      setSnack({ open: true, message: 'Error al revocar', severity: 'error' });
    }
  };

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Gestión de Service‑Keys
      </Typography>

      <Box mb={2} display="flex" alignItems="center" gap={1}>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchKeys}
          disabled={loadingKeys}
        >
          {loadingKeys ? <CircularProgress size={20} /> : 'Refrescar'}
        </Button>
        <Box flexGrow={1} />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenNew(true)}
        >
          Nueva llave
        </Button>
      </Box>

      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Tenant ID</TableCell>
              <TableCell>Descripción</TableCell>
              <TableCell>Creada</TableCell>
              <TableCell>Revocada</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {keys.map(k => (
              <TableRow key={k.id} hover>
                <TableCell>{k.id}</TableCell>
                <TableCell>{k.tenantid}</TableCell>
                <TableCell>{k.description}</TableCell>
                <TableCell>{new Date(k.created_at).toLocaleString()}</TableCell>
                <TableCell>{k.revoked ? 'Sí' : 'No'}</TableCell>
                <TableCell align="right">
                  {!k.revoked && (
                    <IconButton size="small" color="error" onClick={() => handleRevoke(k.id)}>
                      <DeleteIcon />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {keys.length === 0 && !loadingKeys && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No hay llaves para mostrar
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Diálogo Nueva llave */}
      <Dialog open={openNew} onClose={() => setOpenNew(false)} fullWidth maxWidth="xs">
        <DialogTitle>Crear nueva Service‑Key</DialogTitle>
        <DialogContent sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl fullWidth disabled={loadingTenants}>
            <InputLabel id="tenant-select-label">Selecciona Tenant</InputLabel>
            <Select
              labelId="tenant-select-label"
              label="Selecciona Tenant"
              value={selectedTenant}
              onChange={e => setSelectedTenant(e.target.value)}
            >
              {tenants.map(t => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name} — {t.id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Descripción (nombre del tenant)"
            fullWidth
            value={tenants.find(t => t.id === selectedTenant)?.name || ''}
            disabled
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenNew(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating}>
            {creating ? <CircularProgress size={20} /> : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={6000}
        onClose={() => setSnack(prev => ({ ...prev, open: false }))}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack(prev => ({ ...prev, open: false }))}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
